import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Download, BookOpen, FileText, TrendingUp, Home, Lock, Mail, Send, Sparkles, Heart, PenLine, ChevronRight, Star, Calendar, User, FolderOpen, Link2, Video, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { PDF_URL, APP_CONFIG } from '../constants/appConfig';
import chaptersData from '../data/chapters.json';
import FeedbackForm from './FeedbackForm';
import MessagingCenter from './MessagingCenter';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import ScholarshipRequestDialog from './ScholarshipRequestDialog';
import AnnouncementBoard from './AnnouncementBoard';
import PremiumFooter from './PremiumFooter';

function Dashboard() {
  const [user, setUser] = useState(null);
  const [showScholarshipDialog, setShowScholarshipDialog] = useState(false);
  const [progress, setProgress] = useState([]);
  const [responses, setResponses] = useState([]);
  const [journal, setJournal] = useState([]);
  const [summary, setSummary] = useState('');
  const [stats, setStats] = useState({ completed: 0, total_responses: 0 });
  const [newEntry, setNewEntry] = useState({ title: '', content: '' });
  const [activeTab, setActiveTab] = useState('summary');
  const [blogDraft, setBlogDraft] = useState({ title: '', content: '', excerpt: '' });
  const [resources, setResources] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [selectedHomework, setSelectedHomework] = useState(null);

  useEffect(() => {
    const userId = localStorage.getItem('workbook_user_id');
    const userName = localStorage.getItem('workbook_user_name');
    const userEmail = localStorage.getItem('workbook_user_email');
    
    if (!userId || !userEmail) {
      window.location.href = '/login';
      return;
    }
    
    setUser({ id: userId, name: userName, email: userEmail });
    loadUserData(userId);
  }, []);

  const loadUserData = async (userId) => {
    try {
      const progressRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workbook/progress/${userId}`);
      if (progressRes.ok) {
        const data = await progressRes.json();
        setProgress(data.progress || []);
      }

      const responsesRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workbook/responses/${userId}`);
      if (responsesRes.ok) {
        const data = await responsesRes.json();
        setResponses(data.responses || []);
      }

      const journalRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workbook/journal/${userId}`);
      if (journalRes.ok) {
        const data = await journalRes.json();
        setJournal(data.entries || []);
      }

      const summaryRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workbook/summary/${userId}`);
      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data.summary || '');
        setStats(data.stats || { completed: 0 });
      }

      // Fetch student materials (resources + homework combined)
      const materialsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/student/materials`);
      if (materialsRes.ok) {
        const data = await materialsRes.json();
        setResources(data.materials || []);
      }

      // Fetch highlights
      const highlightsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ebook/highlights/all`);
      if (highlightsRes.ok) {
        const data = await highlightsRes.json();
        setHighlights(data.filter(h => h.active) || []);
      }
    } catch (error) {
      console.error('Failed to load user data');
    }
  };

  const addJournalEntry = async () => {
    if (!newEntry.title || !newEntry.content) {
      toast.error('Please fill in both title and content');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workbook/journal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          title: newEntry.title,
          content: newEntry.content,
          tags: []
        })
      });

      if (!response.ok) throw new Error('Failed to save');

      toast.success('Journal entry saved!');
      setNewEntry({ title: '', content: '' });
      loadUserData(user.id);
    } catch (error) {
      toast.error('Failed to save journal entry');
    }
  };

  const downloadPDF = () => {
    const link = document.createElement('a');
    link.href = PDF_URL;
    link.download = APP_CONFIG.workbookPdfFilename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Belonging eBook PDF download started');
  };

  const submitBlogPost = async () => {
    if (!blogDraft.title || !blogDraft.content) {
      toast.error('Please fill in title and content');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/blog-submission`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          user_name: user.name,
          user_email: user.email,
          title: blogDraft.title,
          content: blogDraft.content,
          excerpt: blogDraft.excerpt || blogDraft.content.substring(0, 150),
          status: 'pending_review'
        })
      });

      if (!response.ok) throw new Error('Failed');

      toast.success('Your story has been submitted for review!');
      setBlogDraft({ title: '', content: '', excerpt: '' });
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    }
  };

  if (!user) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-amber-500">Loading...</div>
    </div>
  );

  const totalExercises = chaptersData.chapters.reduce((sum, ch) => sum + ch.exercises.length, 0);
  const overallProgress = totalExercises > 0 ? (stats.completed / totalExercises) * 100 : 0;

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Cinematic Header */}
      <header className="relative overflow-hidden border-b border-amber-500/20">
        {/* Background texture */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(212, 175, 55, 0.4) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-8">
          {/* Scholarship Banner */}
          <div className="mb-6 flex items-center justify-between bg-gradient-to-r from-amber-900/30 to-transparent border border-amber-500/30 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-amber-500" />
              <span className="text-sm text-amber-200">
                Need financial assistance? Request a scholarship to access premium features.
              </span>
            </div>
            <Button
              onClick={() => setShowScholarshipDialog(true)}
              className="bg-amber-600 hover:bg-amber-500 text-black font-semibold rounded-none"
              size="sm"
            >
              Request Scholarship
            </Button>
          </div>

          {/* Main Header */}
          <div className="flex justify-between items-center">
            <div>
              <p className="text-amber-500 text-xs tracking-[0.3em] uppercase mb-2">Welcome back</p>
              <h1 className="text-4xl font-light tracking-wide" style={{ fontFamily: 'Playfair Display, serif' }}>
                {user.name}
              </h1>
              <p className="text-stone-500 text-sm mt-1">{user.email}</p>
            </div>
            <div className="flex gap-3">
              <a href={PDF_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-amber-600 hover:bg-amber-500 text-black font-semibold rounded-none" data-testid="download-pdf-btn">
                  <Download className="w-4 h-4 mr-2" />
                  PDF
                </Button>
              </a>
              <a href="/belonging-ebook">
                <Button className="bg-transparent border border-amber-500/50 text-amber-500 hover:bg-amber-500/20 rounded-none" data-testid="belonging-ebook-btn">
                  <BookOpen className="w-4 h-4 mr-2" />
                  eBook
                </Button>
              </a>
              <a href="/lore">
                <Button className="bg-transparent border border-amber-500/50 text-amber-500 hover:bg-amber-500/20 rounded-none">
                  <FileText className="w-4 h-4 mr-2" />
                  LORE
                </Button>
              </a>
              <a href="/">
                <Button variant="ghost" className="text-stone-500 hover:text-white rounded-none">
                  <Home className="w-4 h-4 mr-2" />
                  Home
                </Button>
              </a>
              <Button 
                onClick={() => { localStorage.clear(); window.location.href = '/'; }}
                variant="ghost"
                className="text-red-500/70 hover:text-red-400 rounded-none"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* eBook Highlights Section - NEW */}
      {highlights.length > 0 && (
        <section className="py-6 px-6 border-b border-stone-800 bg-gradient-to-r from-stone-900/80 to-stone-950/80">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-1.5 rounded bg-emerald-500/20 text-emerald-400">
                <BookOpen className="w-4 h-4" />
              </div>
              <h2 className="text-lg font-light text-emerald-100" style={{ fontFamily: 'Playfair Display, serif' }}>
                This Week's Highlights
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {highlights.map((highlight) => (
                <a
                  key={highlight._id}
                  href={`/belonging-ebook?page=${highlight.page}`}
                  className="group flex items-center gap-4 bg-stone-900 border border-stone-800 hover:border-emerald-500/50 rounded-lg p-3 transition-all hover:bg-stone-800"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-emerald-900/20 border border-emerald-500/20 rounded flex items-center justify-center text-emerald-400 font-serif font-medium text-lg group-hover:scale-105 transition-transform">
                    {highlight.page}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-emerald-500/70 text-[10px] tracking-wider uppercase mb-0.5">
                      {highlight.session_name || 'Featured Page'}
                    </p>
                    <h3 className="text-white font-medium text-sm truncate group-hover:text-emerald-300 transition-colors">
                      {highlight.title}
                    </h3>
                  </div>
                  <ChevronRight className="w-4 h-4 text-stone-600 group-hover:text-emerald-500 transition-colors" />
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Stats Overview - Compressed Size */}
      <section className="py-6 px-6 border-b border-stone-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-4">
            {/* Overall Progress */}
            <div className="bg-gradient-to-br from-stone-900 to-black border border-amber-500/30 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-black" />
                </div>
                <p className="text-stone-400 text-xs">Overall Progress</p>
              </div>
              <p className="text-2xl font-light text-white mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                {Math.round(overallProgress)}%
              </p>
              <div className="h-1.5 bg-stone-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-amber-500 to-amber-600 transition-all duration-1000"
                  style={{ width: `${overallProgress}%` }}
                ></div>
              </div>
            </div>

            {/* Exercises Completed */}
            <div className="bg-gradient-to-br from-stone-900 to-black border border-stone-700 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-stone-800 flex items-center justify-center">
                  <BookOpen className="w-4 h-4 text-amber-500" />
                </div>
                <p className="text-stone-400 text-xs">Exercises</p>
              </div>
              <p className="text-2xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                {stats.completed}
              </p>
              <p className="text-stone-600 text-[10px] mt-1">of {totalExercises} completed</p>
            </div>

            {/* Reflections */}
            <div className="bg-gradient-to-br from-stone-900 to-black border border-stone-700 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-stone-800 flex items-center justify-center">
                  <PenLine className="w-4 h-4 text-amber-500" />
                </div>
                <p className="text-stone-400 text-xs">Reflections</p>
              </div>
              <p className="text-2xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                {stats.total_responses || 0}
              </p>
              <p className="text-stone-600 text-[10px] mt-1">written responses</p>
            </div>

            {/* Journal Entries */}
            <div className="bg-gradient-to-br from-stone-900 to-black border border-stone-700 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-stone-800 flex items-center justify-center">
                  <Heart className="w-4 h-4 text-amber-500" />
                </div>
                <p className="text-stone-400 text-xs">Journal Entries</p>
              </div>
              <p className="text-2xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                {journal.length}
              </p>
              <p className="text-stone-600 text-[10px] mt-1">personal reflections</p>
            </div>
          </div>
        </div>
      </section>

      {/* Announcements */}
      <section className="py-8 px-6 border-b border-stone-800 bg-gradient-to-b from-stone-900/50 to-transparent">
        <div className="max-w-7xl mx-auto">
          <AnnouncementBoard />
        </div>
      </section>

      {/* Featured Resources Section */}
      {resources.length > 0 && (
        <section className="py-8 px-6 border-b border-stone-800 bg-gradient-to-b from-amber-900/10 to-transparent">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <FolderOpen className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <h2 className="text-xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Resources & Materials
                  </h2>
                  <p className="text-stone-500 text-xs">Curated content to support your learning journey</p>
                </div>
              </div>
              <Button 
                onClick={() => setActiveTab('resources')}
                className="bg-transparent border border-amber-500/30 text-amber-500 hover:bg-amber-500/20 rounded-none text-sm"
              >
                View All Resources
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {/* Featured Resource Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {resources.filter(r => r.is_featured).slice(0, 3).map(resource => (
                <a
                  key={resource._id}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-gradient-to-br from-stone-900 to-black border border-amber-500/30 rounded-lg p-5 hover:border-amber-500/50 transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg flex-shrink-0 ${
                      resource.resource_type === 'video' ? 'bg-purple-500/20 text-purple-400' :
                      resource.resource_type === 'document' ? 'bg-blue-500/20 text-blue-400' :
                      resource.resource_type === 'file' ? 'bg-green-500/20 text-green-400' :
                      'bg-amber-500/20 text-amber-400'
                    }`}>
                      {resource.resource_type === 'video' ? <Video className="w-5 h-5" /> :
                       resource.resource_type === 'document' ? <FileText className="w-5 h-5" /> :
                       resource.resource_type === 'file' ? <FolderOpen className="w-5 h-5" /> :
                       <Link2 className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                        <span className="text-amber-500/70 text-xs">Featured</span>
                      </div>
                      <h3 className="text-white font-medium group-hover:text-amber-400 transition-colors truncate">
                        {resource.title}
                      </h3>
                      {resource.description && (
                        <p className="text-stone-500 text-sm mt-1 line-clamp-2">{resource.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-3 text-xs text-stone-600">
                        <span className="px-2 py-0.5 bg-stone-800 rounded">{resource.category}</span>
                        <ExternalLink className="w-3 h-3" />
                      </div>
                    </div>
                  </div>
                </a>
              ))}
              
              {/* Show non-featured if no featured available */}
              {resources.filter(r => r.is_featured).length === 0 && resources.slice(0, 3).map(resource => (
                <a
                  key={resource._id}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-gradient-to-br from-stone-900 to-black border border-stone-700 rounded-lg p-5 hover:border-stone-600 transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg flex-shrink-0 ${
                      resource.resource_type === 'video' ? 'bg-purple-500/20 text-purple-400' :
                      resource.resource_type === 'document' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-teal-500/20 text-teal-400'
                    }`}>
                      {resource.resource_type === 'video' ? <Video className="w-5 h-5" /> :
                       resource.resource_type === 'document' ? <FileText className="w-5 h-5" /> :
                       <Link2 className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-medium group-hover:text-amber-400 transition-colors truncate">
                        {resource.title}
                      </h3>
                      {resource.description && (
                        <p className="text-stone-500 text-sm mt-1 line-clamp-2">{resource.description}</p>
                      )}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Main Content Tabs */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Tab Navigation */}
          <nav className="flex gap-1 mb-10 border-b border-stone-800 pb-1">
            {[
              { id: 'summary', label: 'Journey', icon: Sparkles },
              { id: 'progress', label: 'Progress', icon: TrendingUp },
              { id: 'journal', label: 'Journal', icon: PenLine },
              { id: 'resources', label: 'Resources', icon: FolderOpen },
              { id: 'lore', label: 'My LORE', icon: Star },
              { id: 'messages', label: 'Messages', icon: Mail },
              { id: 'feedback', label: 'Feedback', icon: Send },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 text-sm transition-all border-b-2 ${
                  activeTab === tab.id 
                    ? 'text-amber-500 border-amber-500' 
                    : 'text-stone-500 border-transparent hover:text-stone-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>

          {/* Journey Summary Tab */}
          {activeTab === 'summary' && (
            <div className="space-y-10">
              <div className="bg-gradient-to-br from-stone-900 to-black border border-amber-500/20 rounded-lg p-8">
                <h2 className="text-3xl font-light text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Your NVC Journey
                </h2>
                <p className="text-xl text-stone-300 leading-relaxed">
                  {summary || 'Complete some exercises to see your personalized summary of growth and insights.'}
                </p>
              </div>

              {/* Recent Reflections */}
              <div>
                <h3 className="text-stone-400 text-xs tracking-[0.2em] uppercase mb-6">Recent Reflections</h3>
                {responses.length === 0 ? (
                  <p className="text-stone-500">No reflections yet. Start working through the chapters!</p>
                ) : (
                  <div className="space-y-4">
                    {responses.slice(0, 5).map((r, i) => (
                      <div key={i} className="bg-stone-900/50 border border-stone-800 rounded-lg p-5">
                        <p className="text-amber-500 text-xs mb-2">Chapter {r.chapter_id} • Exercise {r.exercise_id}</p>
                        <p className="text-stone-300">{r.response_text.substring(0, 200)}{r.response_text.length > 200 ? '...' : ''}</p>
                        <p className="text-stone-600 text-xs mt-3">{new Date(r.updated_at).toLocaleDateString()}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Progress Tab */}
          {activeTab === 'progress' && (
            <div className="space-y-6">
              {chaptersData.chapters.map(chapter => {
                const chapterProgress = progress.filter(p => p.chapter_id === chapter.id && p.completed);
                const percent = chapter.exercises.length > 0 ? (chapterProgress.length / chapter.exercises.length) * 100 : 0;
                
                return (
                  <div key={chapter.id} className="bg-stone-900/50 border border-stone-800 rounded-lg p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="text-amber-500 text-xs tracking-wide">CHAPTER {chapter.id}</p>
                        <h3 className="text-xl text-white font-light mt-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                          {chapter.title}
                        </h3>
                      </div>
                      <span className="text-2xl font-light text-amber-500">{Math.round(percent)}%</span>
                    </div>
                    <div className="h-2 bg-stone-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-amber-600 to-amber-500 transition-all duration-500"
                        style={{ width: `${percent}%` }}
                      ></div>
                    </div>
                    <p className="text-stone-500 text-xs mt-3">
                      {chapterProgress.length} of {chapter.exercises.length} exercises completed
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Journal Tab */}
          {activeTab === 'journal' && (
            <div className="space-y-8">
              {/* New Entry Form */}
              <div className="bg-gradient-to-br from-stone-900 to-black border border-amber-500/20 rounded-lg p-8">
                <h3 className="text-xl text-white font-light mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <PenLine className="w-5 h-5 inline mr-3 text-amber-500" />
                  New Journal Entry
                </h3>
                <div className="space-y-4">
                  <Input
                    value={newEntry.title}
                    onChange={(e) => setNewEntry({ ...newEntry, title: e.target.value })}
                    placeholder="Entry title..."
                    className="bg-stone-800/50 border-stone-700 text-white placeholder:text-stone-500 rounded-none focus:border-amber-500"
                  />
                  <Textarea
                    value={newEntry.content}
                    onChange={(e) => setNewEntry({ ...newEntry, content: e.target.value })}
                    placeholder="Write your thoughts, reflections, or insights..."
                    className="bg-stone-800/50 border-stone-700 text-white placeholder:text-stone-500 min-h-[150px] rounded-none focus:border-amber-500"
                  />
                  <Button 
                    onClick={addJournalEntry}
                    className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-black font-semibold rounded-none"
                  >
                    Save Entry
                  </Button>
                </div>
              </div>

              {/* Previous Entries */}
              <div>
                <h3 className="text-stone-400 text-xs tracking-[0.2em] uppercase mb-6">Previous Entries</h3>
                {journal.length === 0 ? (
                  <p className="text-stone-500">No journal entries yet. Start writing!</p>
                ) : (
                  <div className="space-y-4">
                    {journal.map((entry, i) => (
                      <div key={i} className="bg-stone-900/50 border border-stone-800 rounded-lg p-6">
                        <h4 className="text-lg text-white font-light mb-2">{entry.title}</h4>
                        <p className="text-stone-400 text-sm leading-relaxed">{entry.content}</p>
                        <p className="text-stone-600 text-xs mt-4 flex items-center gap-2">
                          <Calendar className="w-3 h-3" />
                          {new Date(entry.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Resources Tab */}
          {activeTab === 'resources' && (
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl text-white font-light mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Learning Materials
                </h3>
                <p className="text-stone-500 text-sm">Resources and homework to support your NVC journey</p>
              </div>

              {/* Featured Resources & Required Homework */}
              {resources.filter(r => r.is_featured).length > 0 && (
                <div className="mb-8">
                  <h4 className="text-lg text-amber-500 font-medium mb-4 flex items-center gap-2">
                    <Star className="w-4 h-4 fill-amber-500" />
                    Featured & Required
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {resources.filter(r => r.is_featured).map(resource => (
                      resource.material_type === 'homework' ? (
                        <button
                          key={resource._id}
                          onClick={() => setSelectedHomework(resource)}
                          className="group text-left bg-gradient-to-br from-amber-500/10 to-stone-900 border border-amber-500/30 rounded-lg p-6 hover:border-amber-500/50 transition-all"
                        >
                          <div className="flex items-start gap-4">
                            <div className="p-3 rounded-lg bg-orange-500/20 text-orange-400">
                              <BookOpen className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="px-2 py-0.5 bg-orange-500/20 rounded text-xs text-orange-400">Homework</span>
                                <span className="text-stone-500 text-xs">{resource.category}</span>
                              </div>
                              <h5 className="text-white font-medium group-hover:text-amber-400 transition-colors">
                                {resource.title}
                              </h5>
                              {resource.description && (
                                <p className="text-stone-400 text-sm mt-1 line-clamp-2">{resource.description}</p>
                              )}
                              <div className="flex items-center gap-3 mt-3">
                                {resource.estimated_time && (
                                  <span className="text-stone-500 text-xs flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {resource.estimated_time}
                                  </span>
                                )}
                                <span className="text-amber-500/70 text-xs">Click to view →</span>
                              </div>
                            </div>
                          </div>
                        </button>
                      ) : (
                        <a
                          key={resource._id}
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group bg-gradient-to-br from-amber-500/10 to-stone-900 border border-amber-500/30 rounded-lg p-6 hover:border-amber-500/50 transition-all"
                        >
                          <div className="flex items-start gap-4">
                            <div className={`p-3 rounded-lg ${
                              resource.resource_type === 'video' ? 'bg-purple-500/20 text-purple-400' :
                              resource.resource_type === 'document' ? 'bg-blue-500/20 text-blue-400' :
                              resource.resource_type === 'file' ? 'bg-green-500/20 text-green-400' :
                              'bg-amber-500/20 text-amber-500'
                            }`}>
                              {resource.resource_type === 'video' ? <Video className="w-5 h-5" /> :
                               resource.resource_type === 'document' ? <FileText className="w-5 h-5" /> :
                               resource.resource_type === 'file' ? <FolderOpen className="w-5 h-5" /> :
                               <Link2 className="w-5 h-5" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h5 className="text-white font-medium group-hover:text-amber-400 transition-colors">
                                {resource.title}
                              </h5>
                              {resource.description && (
                                <p className="text-stone-400 text-sm mt-1 line-clamp-2">{resource.description}</p>
                              )}
                              <div className="flex items-center gap-3 mt-3">
                                <span className="px-2 py-0.5 bg-stone-800 rounded text-xs text-stone-400">{resource.category}</span>
                                <span className="inline-flex items-center gap-1 text-amber-500/70 text-xs">
                                  <ExternalLink className="w-3 h-3" />
                                  Open
                                </span>
                              </div>
                            </div>
                          </div>
                        </a>
                      )
                    ))}
                  </div>
                </div>
              )}

              {/* Homework Assignments Section */}
              {resources.filter(r => r.material_type === 'homework').length > 0 && (
                <div className="mb-8">
                  <h4 className="text-lg text-white font-medium mb-4 pb-2 border-b border-stone-800 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-orange-400" />
                    Homework Assignments
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {resources.filter(r => r.material_type === 'homework' && !r.is_featured).map(resource => (
                      <button
                        key={resource._id}
                        onClick={() => setSelectedHomework(resource)}
                        className="group text-left bg-stone-900/50 border border-stone-800 rounded-lg p-4 hover:border-orange-500/50 hover:bg-stone-900 transition-all"
                      >
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-orange-500/20 text-orange-400 flex-shrink-0">
                            <BookOpen className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-orange-400/70 text-xs">{resource.category}</span>
                            <h5 className="text-stone-200 text-sm font-medium group-hover:text-white truncate">
                              {resource.title}
                            </h5>
                            {resource.estimated_time && (
                              <p className="text-stone-500 text-xs mt-1">{resource.estimated_time}</p>
                            )}
                          </div>
                          <ChevronRight className="w-4 h-4 text-stone-600 group-hover:text-orange-400 flex-shrink-0" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Resources by Type */}
              {resources.length > 0 ? (
                <div className="space-y-10">
                  {/* Links */}
                  {resources.filter(r => r.resource_type === 'link' && !r.is_featured).length > 0 && (
                    <div>
                      <h4 className="text-lg text-white font-medium mb-4 pb-2 border-b border-stone-800 flex items-center gap-2">
                        <Link2 className="w-5 h-5 text-teal-400" />
                        Links & Websites
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {resources.filter(r => r.resource_type === 'link' && !r.is_featured).map(resource => (
                          <a key={resource._id} href={resource.url} target="_blank" rel="noopener noreferrer"
                            className="group flex items-center gap-3 bg-stone-900/50 border border-stone-800 rounded-lg p-4 hover:border-teal-500/50 hover:bg-stone-900 transition-all">
                            <div className="p-2 rounded-lg bg-teal-500/20 text-teal-400 flex-shrink-0">
                              <Link2 className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h5 className="text-stone-200 text-sm font-medium truncate group-hover:text-white">{resource.title}</h5>
                              {resource.description && <p className="text-stone-500 text-xs truncate">{resource.description}</p>}
                            </div>
                            <ExternalLink className="w-4 h-4 text-stone-600 group-hover:text-teal-400 flex-shrink-0" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Documents */}
                  {resources.filter(r => r.resource_type === 'document' && !r.is_featured).length > 0 && (
                    <div>
                      <h4 className="text-lg text-white font-medium mb-4 pb-2 border-b border-stone-800 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-400" />
                        Documents & PDFs
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {resources.filter(r => r.resource_type === 'document' && !r.is_featured).map(resource => (
                          <a key={resource._id} href={resource.url} target="_blank" rel="noopener noreferrer"
                            className="group flex items-center gap-3 bg-stone-900/50 border border-stone-800 rounded-lg p-4 hover:border-blue-500/50 hover:bg-stone-900 transition-all">
                            <div className="p-2 rounded-lg bg-blue-500/20 text-blue-400 flex-shrink-0">
                              <FileText className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h5 className="text-stone-200 text-sm font-medium truncate group-hover:text-white">{resource.title}</h5>
                              {resource.description && <p className="text-stone-500 text-xs truncate">{resource.description}</p>}
                            </div>
                            <Download className="w-4 h-4 text-stone-600 group-hover:text-blue-400 flex-shrink-0" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Videos */}
                  {resources.filter(r => r.resource_type === 'video' && !r.is_featured).length > 0 && (
                    <div>
                      <h4 className="text-lg text-white font-medium mb-4 pb-2 border-b border-stone-800 flex items-center gap-2">
                        <Video className="w-5 h-5 text-purple-400" />
                        Videos
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {resources.filter(r => r.resource_type === 'video' && !r.is_featured).map(resource => (
                          <a key={resource._id} href={resource.url} target="_blank" rel="noopener noreferrer"
                            className="group flex items-center gap-3 bg-stone-900/50 border border-stone-800 rounded-lg p-4 hover:border-purple-500/50 hover:bg-stone-900 transition-all">
                            <div className="p-2 rounded-lg bg-purple-500/20 text-purple-400 flex-shrink-0">
                              <Video className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h5 className="text-stone-200 text-sm font-medium truncate group-hover:text-white">{resource.title}</h5>
                              {resource.description && <p className="text-stone-500 text-xs truncate">{resource.description}</p>}
                            </div>
                            <ExternalLink className="w-4 h-4 text-stone-600 group-hover:text-purple-400 flex-shrink-0" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Files */}
                  {resources.filter(r => r.resource_type === 'file' && !r.is_featured).length > 0 && (
                    <div>
                      <h4 className="text-lg text-white font-medium mb-4 pb-2 border-b border-stone-800 flex items-center gap-2">
                        <FolderOpen className="w-5 h-5 text-green-400" />
                        Files & Downloads
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {resources.filter(r => r.resource_type === 'file' && !r.is_featured).map(resource => (
                          <a key={resource._id} href={resource.url} target="_blank" rel="noopener noreferrer"
                            className="group flex items-center gap-3 bg-stone-900/50 border border-stone-800 rounded-lg p-4 hover:border-green-500/50 hover:bg-stone-900 transition-all">
                            <div className="p-2 rounded-lg bg-green-500/20 text-green-400 flex-shrink-0">
                              <FolderOpen className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h5 className="text-stone-200 text-sm font-medium truncate group-hover:text-white">{resource.title}</h5>
                              {resource.description && <p className="text-stone-500 text-xs truncate">{resource.description}</p>}
                            </div>
                            <Download className="w-4 h-4 text-stone-600 group-hover:text-green-400 flex-shrink-0" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-stone-900/50 border border-stone-800 rounded-lg p-12 text-center">
                  <FolderOpen className="w-12 h-12 text-stone-600 mx-auto mb-4" />
                  <p className="text-stone-400">No resources available yet.</p>
                  <p className="text-stone-500 text-sm mt-2">Check back soon for study materials and helpful links.</p>
                </div>
              )}
            </div>
          )}

          {/* My LORE Tab - Personal Library & Blog Submission */}
          {activeTab === 'lore' && (
            <div className="space-y-10">
              {/* Personal LORE Library */}
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl text-white font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Your Personal LORE Library
                    </h3>
                    <p className="text-stone-500 text-sm mt-1">Your private collection of wisdom and growth moments</p>
                  </div>
                  <a href="/lore">
                    <Button className="bg-transparent border border-amber-500/50 text-amber-500 hover:bg-amber-500/20 rounded-none">
                      View Public LORE
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Journal entries as LORE */}
                  {journal.slice(0, 4).map((entry, i) => (
                    <div key={i} className="bg-stone-900/50 border border-stone-800 rounded-lg p-6 hover:border-amber-500/30 transition-colors">
                      <p className="text-amber-500 text-xs tracking-wide mb-2">PERSONAL REFLECTION</p>
                      <h4 className="text-lg text-white font-light mb-3">{entry.title}</h4>
                      <p className="text-stone-400 text-sm line-clamp-3">{entry.content}</p>
                      <p className="text-stone-600 text-xs mt-4">{new Date(entry.created_at).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit Story to LORE */}
              <div className="bg-gradient-to-br from-amber-900/20 to-stone-900 border border-amber-500/30 rounded-lg p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Star className="w-6 h-6 text-amber-500" />
                  <div>
                    <h3 className="text-xl text-white font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Share Your Story
                    </h3>
                    <p className="text-amber-200/60 text-sm">Submit a story for the public LORE library</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <Input
                    value={blogDraft.title}
                    onChange={(e) => setBlogDraft({ ...blogDraft, title: e.target.value })}
                    placeholder="Story title..."
                    className="bg-stone-800/50 border-stone-700 text-white placeholder:text-stone-500 rounded-none focus:border-amber-500"
                  />
                  <Textarea
                    value={blogDraft.excerpt}
                    onChange={(e) => setBlogDraft({ ...blogDraft, excerpt: e.target.value })}
                    placeholder="Brief description (shown in preview)..."
                    className="bg-stone-800/50 border-stone-700 text-white placeholder:text-stone-500 h-20 rounded-none focus:border-amber-500"
                  />
                  <Textarea
                    value={blogDraft.content}
                    onChange={(e) => setBlogDraft({ ...blogDraft, content: e.target.value })}
                    placeholder="Your story... Share your journey, insights, or transformation through NVC."
                    className="bg-stone-800/50 border-stone-700 text-white placeholder:text-stone-500 min-h-[200px] rounded-none focus:border-amber-500"
                  />
                  <div className="flex justify-between items-center pt-4">
                    <p className="text-stone-500 text-xs">Stories are reviewed by admin before publishing</p>
                    <Button 
                      onClick={submitBlogPost}
                      className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-black font-semibold rounded-none"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Submit for Review
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <div className="bg-stone-900/50 border border-stone-800 rounded-lg p-6">
              <MessagingCenter user={user} />
            </div>
          )}

          {/* Feedback Tab */}
          {activeTab === 'feedback' && (
            <div className="bg-stone-900/50 border border-stone-800 rounded-lg p-6">
              <FeedbackForm userId={user?.id} />
            </div>
          )}
        </div>
      </section>

      {/* Quick Actions Footer */}
      <section className="py-10 px-6 border-t border-stone-800 bg-gradient-to-t from-stone-900/50 to-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={downloadPDF}
              className="bg-transparent border border-stone-700 text-stone-400 hover:text-white hover:border-amber-500 rounded-none"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            <a href="/ofnr-trainer">
              <Button className="bg-transparent border border-stone-700 text-stone-400 hover:text-white hover:border-amber-500 rounded-none">
                OFNR Trainer
              </Button>
            </a>
            <a href="/elchme">
              <Button className="bg-transparent border border-stone-700 text-stone-400 hover:text-white hover:border-amber-500 rounded-none">
                ElchmE Board
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Premium Footer */}
      <PremiumFooter variant="dark" />

      {/* Scholarship Dialog */}
      <ScholarshipRequestDialog 
        open={showScholarshipDialog} 
        onOpenChange={setShowScholarshipDialog}
        user={user}
      />

      {/* Homework Content Dialog */}
      <Dialog open={!!selectedHomework} onOpenChange={() => setSelectedHomework(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto bg-stone-900 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-light flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              <div className="p-2 rounded-lg bg-orange-500/20 text-orange-400">
                <BookOpen className="w-5 h-5" />
              </div>
              {selectedHomework?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedHomework && (
            <div className="space-y-6 mt-4">
              {/* Meta info */}
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="px-3 py-1 bg-orange-500/20 text-orange-400 rounded-full">
                  {selectedHomework.category}
                </span>
                {selectedHomework.estimated_time && (
                  <span className="flex items-center gap-1 text-stone-400">
                    <Calendar className="w-4 h-4" />
                    {selectedHomework.estimated_time}
                  </span>
                )}
                {selectedHomework.page_reference && (
                  <span className="text-stone-500">
                    Reference: {selectedHomework.page_reference}
                  </span>
                )}
              </div>

              {/* Description */}
              {selectedHomework.description && (
                <div className="bg-stone-800/50 border border-stone-700 rounded-lg p-4">
                  <p className="text-stone-300">{selectedHomework.description}</p>
                </div>
              )}

              {/* Instructions */}
              {selectedHomework.instructions && (
                <div>
                  <h4 className="text-amber-500 font-medium mb-2 text-sm uppercase tracking-wide">Instructions</h4>
                  <p className="text-stone-300 leading-relaxed">{selectedHomework.instructions}</p>
                </div>
              )}

              {/* Main Content */}
              {selectedHomework.content && (
                <div>
                  <h4 className="text-amber-500 font-medium mb-3 text-sm uppercase tracking-wide">Content</h4>
                  <div className="bg-stone-800/30 border border-stone-700/50 rounded-lg p-6">
                    <div className="prose prose-invert prose-stone max-w-none">
                      <div className="text-stone-200 leading-relaxed whitespace-pre-wrap">
                        {selectedHomework.content}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Close button */}
              <div className="flex justify-end pt-4 border-t border-stone-700">
                <Button 
                  onClick={() => setSelectedHomework(null)}
                  className="bg-amber-600 hover:bg-amber-500 text-black font-medium rounded-none"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Dashboard;
